import { useCallback, useState } from 'react';

import { Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Dialog } from '@opendoor/atoms/dialog';
import { Text } from '@opendoor/atoms/text';
import { Toasty } from '@opendoor/atoms/toast';
import { IconMail, IconMessage, IconX } from '@tabler/icons-react';
import type { PropertyDetailsFragment } from '__generated__/athena/graphql';
import { corePdpLink } from 'components/helpers/links';
import { useTrackProperty } from 'components/marketplace/helpers/useTrackProperty';
import { CopyLinkToast } from 'components/marketplace/shared/CopyLinkToast';

import { useObservability } from '../../../helpers/observability';
import { formatStreetAddress } from '../helpers/addressHelpers';

function getShareableContent(slug: string, addressToken: string) {
  return `Check out this home for sale on Opendoor!\n\n${getShareableLink(slug, addressToken)}`;
}

function getShareableLink(slug: string, addressToken: string) {
  return `www.opendoor.com${corePdpLink(slug, addressToken)}`;
}

function getEmailLink(slug: string, addressToken: string) {
  return `mailto:?body=${encodeURIComponent(getShareableContent(slug, addressToken))}`;
}

function getTextMessageLink(slug: string, addressToken: string) {
  return `sms:?&body=${encodeURIComponent(getShareableContent(slug, addressToken))}`;
}

const TOAST_DURATION = 1000; // 1 second

type ShareableProperty = Pick<
  PropertyDetailsFragment,
  'addressToken' | 'slug' | 'street' | 'unitNumber' | 'unitType'
>;

export const ShareModal = ({
  property,
  isOpen,
  onClose,
}: {
  property: ShareableProperty;
  isOpen: boolean;
  onClose: () => void;
}) => {
  const [isToastOpen, setIsToastOpen] = useState(false);
  const { trackProperty } = useTrackProperty();
  const { trackJourneyEvent } = useObservability();
  const formattedStreet = formatStreetAddress(
    property.street,
    property.unitType,
    property.unitNumber,
  );
  const copyToClipboard = (text: string) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
    }
  };

  const handleOnShare = useCallback(() => {
    trackProperty('property-share', 'marketplace-pdp-copy');
    if (property.addressToken) {
      trackJourneyEvent('marketplaceProperty', 'share', {
        addressUuid: property.addressToken,
        source: 'copy',
      });
    }
    copyToClipboard(getShareableLink(property.slug, property.addressToken));
    setIsToastOpen(true);
    onClose();
  }, []);

  const handleSms = useCallback(() => {
    trackProperty('property-share', 'marketplace-pdp-sms');
    window.open(getTextMessageLink(property.slug, property.addressToken), '_self');
    trackJourneyEvent('marketplaceProperty', 'share', {
      addressUuid: property.addressToken,
      source: 'sms',
    });
  }, []);

  const handleEmail = useCallback(() => {
    trackProperty('property-share', 'marketplace-pdp-email');
    trackJourneyEvent('marketplaceProperty', 'share', {
      addressUuid: property.addressToken,
      source: 'email',
    });
    window.open(getEmailLink(property.slug, property.addressToken), '_self');
  }, []);

  const closeToast = useCallback(() => {
    setIsToastOpen(false);
  }, []);

  return (
    <Toasty>
      <Dialog.Root
        role="dialog"
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
      >
        <Dialog hideCloseButton>
          <Dialog.Title className="sr-only">Share</Dialog.Title>
          <Col className="w-full md:w-75">
            <Col className="pb-6">
              <Row className="items-center justify-between">
                <Text as="p" className="text-subheader-sm">
                  Share
                </Text>
                <Button
                  variant="secondary"
                  aria-label="close"
                  analyticsName="close-share-modal"
                  onClick={onClose}
                  className="size-9 rounded-full border-od-border-tertiary p-0"
                >
                  <IconX size={24} />
                </Button>
              </Row>
              <Text as="p" className="pt-1 text-body-sm text-od-content-secondary">
                {formattedStreet}
              </Text>
            </Col>
            <Row
              onClick={handleOnShare}
              analyticsName="property-share-copy"
              aria-label="Copy to clipboard"
              className="items-center justify-between border-b border-od-border-secondary py-6 transition-transform active:scale-[0.98]"
            >
              <Text as="span" className="text-body-sm">
                Copy
              </Text>
              <ContentCopyIcon size={16} />
            </Row>
            <Row
              onClick={handleSms}
              analyticsName="property-share-email"
              aria-label="Share by SMS"
              className="items-center justify-between border-b border-od-border-secondary py-6 transition-transform active:scale-[0.98]"
            >
              <Text as="span" className="text-body-sm">
                SMS
              </Text>
              <IconMessage size={16} />
            </Row>
            <Row
              onClick={handleEmail}
              analyticsName="property-share-email"
              aria-label="Share in Email"
              className="items-center justify-between py-6 transition-transform active:scale-[0.98]"
            >
              <Text as="span" className="text-body-sm">
                Email
              </Text>
              <IconMail size={16} />
            </Row>
          </Col>
        </Dialog>
      </Dialog.Root>
      <CopyLinkToast
        open={isToastOpen}
        message="Link copied to clipboard"
        onShown={closeToast}
        durationMs={TOAST_DURATION}
      />
    </Toasty>
  );
};

function ContentCopyIcon({ size = 24 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <mask id="path-1-inside-1_3070_193338" fill="white">
        <rect x="0.5" y="3.5" width="12.5" height="16.25" rx="1.25" />
      </mask>
      <rect
        x="0.5"
        y="3.5"
        width="12.5"
        height="16.25"
        rx="1.25"
        stroke="black"
        strokeWidth="3.75"
        mask="url(#path-1-inside-1_3070_193338)"
      />
      <path
        d="M4.25 1H14.25C14.9404 1 15.5 1.55964 15.5 2.25V16"
        stroke="black"
        strokeWidth="1.875"
      />
    </svg>
  );
}
